/**
 * Represents a single parsed field from the JSON string.
 */
export interface ParsedJsonField {
  key: string;
  value: string | number | boolean | null;
}

/**
 * Result returned by `parseJsonString`.
 */
export interface ParseJsonResult {
  /** Whether the input was valid JSON and could be parsed. */
  success: boolean;
  /** Ordered list of key-value pairs extracted from the JSON object. */
  fields: ParsedJsonField[];
  /** A human-readable, multi-line representation of the parsed data. */
  readable: string;
  /** The original raw string received. */
  raw: string;
  /** Error message when `success` is false. */
  error?: string;
}

/**
 * Parses a JSON-encoded string returned by the API and converts it into a
 * structured, human-readable format.
 *
 * The function handles doubly-serialised strings (i.e. the API returns a
 * JSON string that is itself wrapped in quotes), as well as plain JSON objects.
 *
 * @param input - The raw string received from the API.
 * @returns A `ParseJsonResult` object with the parsed fields and readable text.
 *
 * @example
 * const raw = '"{\"Tarea\":44720200,\"Caso\":648003}"';
 * const result = parseJsonString(raw);
 * // result.fields → [{ key: "Tarea", value: 44720200 }, { key: "Caso", value: 648003 }]
 * // result.readable → "Tarea: 44720200\nCaso: 648003"
 */
export function parseJsonString(input: string): ParseJsonResult {
  const trimmed = input.trim();

  // ── Step 1: unwrap outer quotes produced by double-serialisation ──────────
  // The API sometimes returns: "\"{ ... }\""  (a JSON string whose value is
  // itself a JSON object string).  We detect this by checking whether the
  // trimmed value starts and ends with a double-quote and attempting to
  // JSON.parse it first to get the inner string.
  let jsonCandidate = trimmed;

  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    try {
      const unwrapped = JSON.parse(trimmed);
      if (typeof unwrapped === 'string') {
        jsonCandidate = unwrapped;
      }
    } catch {
      // Not a valid JSON string wrapper — keep using trimmed as-is.
    }
  }

  // ── Step 2: parse the actual JSON object ──────────────────────────────────
  let parsed: Record<string, unknown>;

  try {
    const result = JSON.parse(jsonCandidate);

    if (result === null || typeof result !== 'object' || Array.isArray(result)) {
      return {
        success: false,
        fields: [],
        readable: '',
        raw: input,
        error: 'El string parseado no es un objeto JSON válido.',
      };
    }

    parsed = result as Record<string, unknown>;
  } catch (err) {
    return {
      success: false,
      fields: [],
      readable: '',
      raw: input,
      error: `No se pudo parsear el string como JSON: ${(err as Error).message}`,
    };
  }

  // ── Step 3: build the fields array and readable text ─────────────────────
  const fields: ParsedJsonField[] = Object.entries(parsed).map(([key, value]) => ({
    key,
    value: value as string | number | boolean | null,
  }));

  const readable = fields
    .map(({ key, value }) => `${key}: ${value ?? '—'}`)
    .join('\n');

  return {
    success: true,
    fields,
    readable,
    raw: input,
  };
}

/**
 * Convenience wrapper that returns only the human-readable string.
 * Returns the original input unchanged when parsing fails.
 *
 * @param input - The raw string received from the API.
 * @returns A formatted, multi-line string or the original input on failure.
 */
export function toReadableJsonString(input: string): string {
  const result = parseJsonString(input);
  return result.success ? result.readable : input;
}
